/**
 * SigningOutSplash — full-screen cover shown the instant a user clicks
 * "Sign out", before the async logout + WorkOS redirect round-trip runs.
 *
 * Why this exists: the web sign-out handler does
 *   await invoke('cipherops/auth/logout')  →  window.location.href = <WorkOS logout URL>
 * Both steps are network round-trips, and until the browser actually leaves
 * the page the authenticated app stays fully rendered on screen — so for
 * ~a second after clicking Sign Out the user still *sees* the application,
 * which reads as "it didn't sign me out immediately". Rendering this cover
 * synchronously (via flushSync in the handler) hides the app the moment the
 * button is pressed, so sign-out feels instant. The session still terminates
 * exactly as before; this is purely the visual gap.
 *
 * Inline styles + Citadel brand tokens (not host CSS vars) so it paints
 * correctly regardless of the surrounding theme — same rationale as
 * SplashScreen.
 */

import React from 'react';

export function SigningOutSplash() {
  return (
    <div style={styles.cover} role="status" aria-live="polite" data-debug-id="signing-out-splash">
      <div style={styles.accentRule} />
      <div style={styles.message}>Signing out&hellip;</div>
      <div style={styles.brand}>Pentest Solutions</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  cover: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    background: '#f6f4f0',
    color: '#1a1f2e',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    fontFamily: 'Georgia, serif',
  },
  accentRule: { width: 44, height: 1.5, background: '#8b1a2e' },
  message: { fontFamily: 'Georgia, serif', fontSize: 20, letterSpacing: '0.3px' },
  brand: {
    fontFamily: '"Courier New", monospace',
    fontSize: 10,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#7a7d8a',
  },
};

export default SigningOutSplash;
